import {
  Avatar,
  FlexBox,
  Spinner,
  Table,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { SearchUserEnriched } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { ConversationAndRequester, User } from 'lib-shared-types-api';
import { MouseEvent, useCallback, useMemo } from 'react';

import { AwayStatus } from 'apis/presence';
import {
  ConversationsTableConversationRow,
  ConversationsTableHeadRow,
} from 'components/conversation/conversationTable';
import { CustomAwayStatus } from 'components/presence/components/customAwayStatus';
import {
  getPresenceFlags,
  getStatus,
  getWorkingChannels,
} from 'components/presence/utils';
import { getUserDisplayName } from 'utils/user';

import { canMakeInternalCall } from '../../utils';
import { PresencePopover } from '../presencePopover';
import { TeamCard } from '../teamCard';

import { Actions, TeamChannelIconsList } from './components';

type TeamTableRowProps = {
  member: SearchUserEnriched;
  favorite?: { isFavorite: boolean; onToggle: () => void };
  isOwnAgent: boolean;
  hasPresenceAccess: boolean;
  hasOrganizationMemberAccess: boolean;
  hasConversationReplyAccess: boolean;
  isMicrophoneBlocked: boolean;
  showUserModal: (user: Partial<User>) => void;
  awayStatuses: AwayStatus[];
  isExpanded: boolean;
  onToggle: (e: MouseEvent<HTMLElement>) => void;
  conversations: readonly ConversationAndRequester[];
  conversationsLoading: boolean;
  twilioMakeInternalCall: (userId: string) => Promise<void>;
};

export const TeamTableRow = ({
  member,
  favorite,
  isOwnAgent,
  hasPresenceAccess,
  hasOrganizationMemberAccess,
  hasConversationReplyAccess,
  isMicrophoneBlocked,
  showUserModal,
  awayStatuses,
  isExpanded,
  onToggle,
  conversations,
  conversationsLoading,
  twilioMakeInternalCall,
}: TeamTableRowProps): JSX.Element => {
  const onCallClick = useCallback(async () => {
    ampli.teamAgentActionsMenuItemClicked({ type: 'call' });
    try {
      await twilioMakeInternalCall(member.id);
    } catch {
      // Silently ignore errors
    }
  }, [member.id, twilioMakeInternalCall]);

  const onEditClick = useCallback(() => {
    const user: Partial<User> = {
      id: member.id,
      name: member.name,
      family_name: member.family_name,
      given_name: member.given_name,
      company_id: member.company_id,
      email: member.email,
      additional_emails: member.additional_emails?.map((x) => x),
      phone_number: member.phone_number,
      default_outbound_number: member.phone_number,
      additional_phone_numbers: member.additional_phone_numbers?.map((x) => x),
      type: member.type,
      avatar_url: member.avatar_url,
    };
    showUserModal(user);
    ampli.teamAgentActionsMenuItemClicked({ type: 'edit' });
  }, [member, showUserModal]);

  const { hasActivePhoneConversation, isInACW, simultaneousRingEnabled } =
    getPresenceFlags(member);
  const canCall = canMakeInternalCall({
    member,
    isOwnAgent,
    isMicrophoneBlocked,
  });

  const status = getStatus(member);
  const workingChannels = getWorkingChannels(member);

  const isOpen = isExpanded;

  const customAwayStatus = useMemo(() => {
    if (status !== 'away' || !awayStatuses.length) {
      return null;
    }

    const awayStatus = awayStatuses.find(
      ({ id }) => id === member.custom_away_status?.custom_away_status_id,
    );

    if (!awayStatus) {
      return null;
    }

    return (
      <CustomAwayStatus
        awayStatus={awayStatus}
        statusUpdatedAt={member.custom_away_status?.status_updated_at || null}
        showLabel={true}
      />
    );
  }, [status, awayStatuses, member.custom_away_status]);

  const memberName = `${getUserDisplayName(member)}${isOwnAgent ? ' (you)' : ''}`;

  const actions =
    isOwnAgent || hasOrganizationMemberAccess || hasConversationReplyAccess ? (
      <Actions
        hasConversationReplyAccess={hasConversationReplyAccess}
        hasOrganizationMemberAccess={hasOrganizationMemberAccess}
        canMakeInternalCall={canCall}
        isOwnAgent={isOwnAgent}
        onCallClick={onCallClick}
        onEditClick={onEditClick}
      />
    ) : undefined;

  return (
    <TeamCard isOpen={isOpen}>
      <TeamCard.Header
        iconsList={
          <div>
            <TeamChannelIconsList
              workloads_by_channel={member.workloads_by_channel}
              working_media_types={member.working_media_types}
              workload_config={member.workload_config}
              status={status}
              simultaneousRingEnabled={simultaneousRingEnabled}
            />
            {hasPresenceAccess && (
              <PresencePopover
                user={{
                  id: member.id,
                  name: member.name || '',
                }}
                presence={{
                  status: status,
                  working_channels: workingChannels,
                }}
              />
            )}
          </div>
        }
        actions={actions}
        status={status}
        avatar={
          <Tooltip content={memberName}>
            <Avatar
              user={{
                id: member.id,
                name: memberName,
                avatar_url: member.avatar_url,
              }}
            />
          </Tooltip>
        }
        favorite={favorite}
        isOpen={isOpen}
        onToggle={onToggle}
        onPhone={hasActivePhoneConversation}
        simultaneous={simultaneousRingEnabled}
        inAcw={isInACW}
      >
        <FlexBox gap="300" alignItems="center">
          {memberName}
          {customAwayStatus}
        </FlexBox>
      </TeamCard.Header>
      <TeamCard.Content>
        <FlexBox justifyContent="center" alignItems="center" padding="0 300">
          {conversationsLoading ? (
            <Spinner size={16} />
          ) : conversations.length > 0 ? (
            <Table>
              <Table.Thead>
                <ConversationsTableHeadRow isInitialized={true} />
              </Table.Thead>
              <Table.Tbody>
                {conversations.map((conversationAndRequester) => (
                  <ConversationsTableConversationRow
                    key={conversationAndRequester.conversation.id}
                    conversationAndRequester={conversationAndRequester}
                    showRowMenuFilter={false}
                  />
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            'No conversations found'
          )}
        </FlexBox>
      </TeamCard.Content>
    </TeamCard>
  );
};
